import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Skeleton } from 'antd';
import { useDispatch, useStore } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ISessionKey } from '@share/common-types';
import { Routes } from '@share/constants';
import { RootState } from '@share/utils';
import type { StripeExpressVertical } from '../../../share/utils/stripe-return-url';

import { getBookingStripe as resumeHotelStripeExpress } from '../../../store/slices/review-book';
import { getBookingStripe as resumeCondoStripeExpress } from '../../../store/slices/condo-review-book';
import { getCarsBookingStripe } from '../../../store/slices/cars-review-book';
import { getWeeksBookingStripe } from '../../../store/slices/weeks/weeks-review-book';

import './style.scss';

function buildSessionKey(sessionId: string): ISessionKey {
  return { value: sessionId, isInvalid: false, expireDate: '' };
}

function fallbackRoute(vertical: StripeExpressVertical): string {
  switch (vertical) {
    case 'hotel':
      return Routes.Search;
    case 'condo':
      return Routes.CondoSearch;
    case 'weeks':
      return Routes.WeeksSearch;
    case 'cars':
      return Routes.CarsSearch;
    default:
      return Routes.Search;
  }
}

export interface IStripeExpressReturnPageProps {
  vertical: StripeExpressVertical;
}

export function StripeExpressReturnPage(props: IStripeExpressReturnPageProps): React.ReactElement | null {
  const { vertical } = props;
  const { accountUsername } = useParams<{ accountUsername: string }>();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore() as { getState: () => RootState };

  const [uiLoading, setUiLoading] = useState(true);
  const [parseError, setParseError] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const processedKeyRef = useRef<string | null>(null);

  const navigateAfterResume = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');
    if (returnTo) {
      try {
        const decoded = decodeURIComponent(returnTo);
        const path = decoded.startsWith('/') ? decoded : `/${decoded}`;
        history.replace(`/${accountUsername}${path}`);
        return;
      } catch {
        /* fall through */
      }
    }
    history.replace(`/${accountUsername}${fallbackRoute(vertical)}`);
  }, [accountUsername, history, location.search, vertical]);

  useEffect(() => {
    const effectKey = `${vertical}|${location.search}`;
    if (processedKeyRef.current === effectKey) {
      return;
    }
    processedKeyRef.current = effectKey;

    const params = new URLSearchParams(location.search);
    const bookingId = params.get('bookingId');
    const sessionId = params.get('sessionId') ?? '';
    const paymentMethod = params.get('paymentMethod') ?? 'Card';
    const redirectStatus = params.get('redirect_status');
    const isCancel = redirectStatus === 'failed' || redirectStatus === 'canceled';

    if (!bookingId) {
      setParseError('Missing booking reference.');
      setUiLoading(false);
      return;
    }

    if (vertical !== 'cars' && !sessionId) {
      setParseError('Missing session.');
      setUiLoading(false);
      return;
    }

    const sessionKey = buildSessionKey(sessionId);

    (async () => {
      try {
        if (vertical === 'hotel') {
          await dispatch(resumeHotelStripeExpress(bookingId, sessionKey, paymentMethod, isCancel) as any);
        } else if (vertical === 'condo') {
          await dispatch(resumeCondoStripeExpress(bookingId, sessionKey, paymentMethod, isCancel) as any);
        } else if (vertical === 'weeks') {
          await dispatch(getWeeksBookingStripe(bookingId, sessionKey, paymentMethod, isCancel) as any);
        } else {
          await dispatch(getCarsBookingStripe(bookingId, sessionKey, paymentMethod, isCancel) as any);
        }

        const s = store.getState();
        let err: string | null = null;
        if (vertical === 'hotel') {
          const r = s.reviewBookStore;
          err = r.stripeError || r.errorBooking || null;
        } else if (vertical === 'condo') {
          const r = s.condoReviewBookStore;
          err = r.errorStripe || r.errorBooking || null;
        } else if (vertical === 'weeks') {
          err = s.weeksReviewBookStore.errorBooking || null;
        } else {
          const r = s.carsReviewBookStore;
          err = r.errorStripe || r.errorBooking || null;
        }

        if (err) {
          setResumeError(err);
        } else {
          navigateAfterResume();
        }
      } catch {
        setResumeError('Payment confirmation failed.');
      } finally {
        setUiLoading(false);
      }
    })();
  }, [dispatch, location.search, navigateAfterResume, store, vertical]);

  if (uiLoading) {
    return (
      <div className="stripe-express-return stripe-express-return--loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (parseError) {
    return (
      <div className="stripe-express-return">
        <Alert type="error" message={parseError} showIcon />
      </div>
    );
  }

  if (resumeError) {
    return (
      <div className="stripe-express-return">
        <Alert
          type="error"
          message={resumeError}
          showIcon
          action={
            <button type="button" className="stripe-express-return__link" onClick={navigateAfterResume}>
              Return to booking
            </button>
          }
        />
      </div>
    );
  }

  return null;
}
